import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { App as AntdApp, ConfigProvider } from 'antd';
import i18n from './i18n';
import './i18n';
import React from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OnboardingFlowProvider } from './contexts/OnboardingFlowContext';

// Pages
import Dashboard from './pages/Dashboard';
import OnboardingPage from './pages/Onboarding';
import { ResourcesRouter } from './pages/resources/ResourcesRouter';
import { FinanceRouter } from './pages/finance/FinanceRouter';
import ResidentsRouter from './pages/residents/ResidentsRouter';
import Users from './pages/Users';
import { Settings } from './pages/Settings';
import { Login, Register, CompleteSignUp, SetPassword, SelectWorkspace } from './pages/Auth';
import InviteAccept from './pages/Auth/InviteAccept';
import UserDetailsPage from './pages/Users/UserDetailsPage';
import WorkspacePage from './pages/Workspace';
import Documents from './pages/Documents';
import Help from './pages/Help';
import Notifications from './pages/Notifications';
import { CopilotAssistant } from './components/copilot/CopilotAssistant';

// Community Router
import CommunityRouter from './pages/community/CommunityRouter';

// Employee Pages
import { EmployeeRouter } from './pages/employee/EmployeeRouter';

// Maintenance Pages
import Maintenance from './pages/maintenance/Maintenance';
import MaintenanceDetailsPage from './pages/maintenance/MaintenanceDetailsPage';

// Layout
import AdminLayout from './layouts/AdminLayout';
import { Spin } from 'antd';

const queryClient = new QueryClient();

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { firestoreUser, loading, firebaseUser } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!firebaseUser) {
    return <Navigate to="/login" replace />;
  }

  // Firebase user but no workspace selected -> must go to select-workspace
  if (firebaseUser && !firestoreUser) {
    return <Navigate to="/select-workspace" replace />;
  }

  return <>{children}</>;
};

// Logout handler component
const Logout = () => {
  const { signOut } = useAuth();
  React.useEffect(() => {
    signOut().finally(() => {
      window.location.replace('/login');
    });
  }, [signOut]);
  return null;
};

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontSize: 13, // Base font size - reduced from 14
          fontSizeSM: 11, // Small font size - reduced from 12
          fontSizeLG: 14, // Large font size - reduced from 16
          fontSizeXL: 16, // Extra large font size - reduced from 18
          fontSizeHeading1: 32, // H1 font size - reduced from 38
          fontSizeHeading2: 26, // H2 font size - reduced from 30
          fontSizeHeading3: 20, // H3 font size - reduced from 24
          fontSizeHeading4: 18, // H4 font size - reduced from 20
          fontSizeHeading5: 14, // H5 font size - reduced from 16
        },
      }}
    >
      <AntdApp>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <AuthProvider>
            <OnboardingFlowProvider>
              <BrowserRouter>
                <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/auth/complete-signup" element={<CompleteSignUp />} />
                <Route path="/auth/set-password" element={<SetPassword />} />
                <Route path="/select-workspace" element={<SelectWorkspace />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/invite" element={<InviteAccept />} />

                {/* Protected routes with AdminLayout as parent */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AdminLayout>
                        <Outlet />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                >
                  <Route path="/" element={<Navigate to="/onboarding" replace />} />
                  <Route path="onboarding" element={<OnboardingPage />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  
                  {/* Resource Management Routes */}
                  <Route path="resources/*" element={<ResourcesRouter />} />

                  {/* Community Routes */}
                  <Route path="community/*" element={<CommunityRouter />} />

                  {/* Employee Management Routes */}
                  <Route path="employee/*" element={<EmployeeRouter />} />
                  
                  {/* Maintenance Routes */}
                  <Route path="maintenance">
                    <Route index element={<Maintenance />} />
                    <Route path=":id" element={<MaintenanceDetailsPage />} />
                  </Route>
                  
                  {/* User Management Routes */}
                  <Route path="users">
                    <Route index element={<Users />} />
                    <Route path=":id" element={<UserDetailsPage />} />
                  </Route>
                  
                  {/* Settings and Workspace Routes */}
                  <Route path="settings" element={<Settings />} />
                  <Route path="workspace" element={<WorkspacePage />} />

                  {/* Finance Routes */}
                  <Route path="finance/*" element={<FinanceRouter />} />

                  {/* Resident Management Routes */}
                  <Route path="residents/*" element={<ResidentsRouter />} />
                  
                  {/* Documents Management Routes */}
                  <Route path="documents" element={<Documents />} />
                  
                  {/* Help and Support Route */}
                  <Route path="help" element={<Help />} />
                  
                  {/* Notifications Route */}
                  <Route path="notifications" element={<Notifications />} />
                </Route>
                </Routes>

                <CopilotAssistant />
              </BrowserRouter>
            </OnboardingFlowProvider>
          </AuthProvider>
        </I18nextProvider>
      </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
